/*
 * "Upozornit mě" dialog (back-in-stock / price-drop alerts) - the custom element.
 *
 * Lives here, not inline in snippets/stock-alert-dialog.liquid, so the browser downloads,
 * compiles and caches it ONCE instead of receiving the same 23 KB inside the HTML of every
 * product page view. The markup, the data and the full design notes (consent handling, the
 * size-run rules, the portal contract) stay in that snippet - read its header first.
 */
(() => {
  if (customElements.get('stock-alert-dialog')) return;

  const EMAIL_STORAGE_KEY = 'pk_alert_email';
  const REQUEST_TIMEOUT_MS = 12000;
  // Texts come from the theme's language files (window.pkT, layout/theme.liquid); the Czech
  // fallback keeps everything working if a key is ever missing. [name] = a value filled in here.
  function tr(key, fallback, vars) {
    let text = (window.pkT && window.pkT[key]) || fallback;
    if (vars) Object.keys(vars).forEach((name) => { text = text.split('[' + name + ']').join(vars[name]); });
    return text;
  }

  const GENERIC_ERROR = tr('alertErrorGeneric', 'Něco se nepovedlo. Zkus to prosím znovu.');
  const EMAIL_PATTERN = /^[^ @]+@[^ @]+[.][^ @]{2,}$/;
  const SUBMIT_LABEL = tr('alertSubmit', 'Upozornit mě');
  const CHECK_ICON =
    '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';

  const HINTS = {
    back_in_stock: tr('alertHintBisSize', 'Pošleme ti e-mail, jakmile bude vybraná velikost znovu skladem.'),
    price_drop: tr('alertHintPdSize', 'Ozveme se, když se objeví vybraná velikost za nižší cenu, než je teď.'),
  };
  // The same for a product without sizes
  const HINTS_LONE = {
    back_in_stock: tr('alertHintBis', 'Pošleme ti e-mail, jakmile bude znovu skladem.'),
    price_drop: tr('alertHintPd', 'Ozveme se, když bude za nižší cenu, než je teď.'),
  };

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  // Variant prices on the page are in the visitor's currency; format them like the rest of the shop
  function formatPagePrice(cents) {
    return window.pkMoney ? window.pkMoney(cents) : formatMoney(cents);
  }

  // Prices coming back from the alert service stay in Czech crowns, as before
  function formatMoney(cents) {
    const value = Number(cents) / 100;
    const whole = Number.isInteger(value);
    return (
      value.toLocaleString('cs-CZ', {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }) + String.fromCharCode(160) + 'Kč'
    );
  }

  // Links coming back from the portal are only followed if they stay on this site.
  function sameOriginPath(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin ? parsed.pathname + parsed.search : null;
    } catch (_) {
      return null;
    }
  }

  // No personal data (never the e-mail) goes into analytics.
  function track(event, data) {
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event }, data));
    } catch (_) {}
  }

  // Runs `callback` a few seconds after the page has finished loading, in an idle moment.
  // requestIdleCallback does not exist in Safari, so it is feature-checked, never assumed.
  function afterPageSettled(callback) {
    const run = () =>
      setTimeout(() => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 4000 });
        else callback();
      }, 3000);
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  class StockAlertDialog extends HTMLElement {
    constructor() {
      super();
      this.state = { tab: 'back_in_stock', selected: new Set() };
      this.busy = false;
      this.onOpenRequest = (event) => this.open(event.detail || {});
    }

    connectedCallback() {
      document.addEventListener('stock-alert:open', this.onOpenRequest);
      if (this.ready) return;
      this.ready = true;

      this.dialog = this.querySelector('dialog');
      this.formView = this.querySelector('[data-sa-view="form"]');
      this.doneView = this.querySelector('[data-sa-view="done"]');
      this.productEl = this.querySelector('[data-sa-product]');
      this.hintEl = this.querySelector('[data-sa-hint]');
      this.sizesEl = this.querySelector('[data-sa-sizes]');
      this.sizesLabelEl = this.querySelector('#sa-sizes-label');
      this.emptyEl = this.querySelector('[data-sa-empty]');
      this.form = this.querySelector('[data-sa-form]');
      this.emailEl = this.querySelector('#sa-email');
      this.errorEl = this.querySelector('[data-sa-error]');
      this.submitEl = this.querySelector('[data-sa-submit]');
      this.demoEl = this.querySelector('[data-sa-demo]');
      this.scrollEl = this.querySelector('[data-sa-scroll]');
      this.legendEl = this.querySelector('[data-sa-legend]');
      this.marketingEl = this.querySelector('[data-sa-marketing]');
      this.marketingTextEl = this.querySelector('[data-sa-marketing-text]');
      this.tabs = Array.from(this.querySelectorAll('[data-sa-tab]'));

      const endpoint = (this.dataset.endpoint || '').trim();
      this.endpoint = /^https:[/][/]/i.test(endpoint) ? endpoint : '';
      // Forced on by the block's own "demo mode" toggle (reviewing the full
      // UI on a draft before the portal is ready) OR simply because no real
      // endpoint is configured - either way, nothing is ever actually sent.
      this.demo = !this.endpoint || this.dataset.demoMode === 'true';
      this.consentVersion = this.dataset.consentVersion || '';
      this.marketingVersion = this.dataset.marketingVersion || '';

      try {
        this.data = JSON.parse(this.querySelector('[data-sa-data]').textContent);
      } catch (_) {
        this.data = null;
      }
      this.sizes = this.data ? this.buildSizeList() : [];
      this.scheduleSizeRun();

      this.bind();
    }

    disconnectedCallback() {
      document.removeEventListener('stock-alert:open', this.onOpenRequest);
    }

    // The product's variants, plus any extra sizes passed in `ladderOverride`
    // - the live size-run response for this exact SKU (see fetchSizeRun).
    // There is no other source for extras: called with nothing, this
    // returns just the real variants, which is exactly right while the
    // size-run request hasn't resolved yet, or resolved to zero sizes.
    buildSizeList(ladderOverride) {
      const isNumeric = (value) => /^[0-9]+([.,][0-9]+)?$/.test(String(value).trim());
      const normalize = (value) => String(parseFloat(String(value).replace(',', '.')));

      const variants = this.data.variants.map((variant) => Object.assign({ extra: false }, variant));
      if (variants.length === 0) return variants;
      // A product without sizes has one variant called "Default Title". That name is never shown:
      // when the portal knows sizes for this SKU only those are offered, otherwise the dialog
      // watches the product itself with no size step (see renderSizes).
      const lone = variants.length === 1 && /^default title$/i.test(String(variants[0].size).trim());
      if (lone) variants[0].lone = true;

      const sku = (variants.find((variant) => variant.sku) || {}).sku || '';
      const ladderRaw = (ladderOverride || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      if (variants.every((variant) => isNumeric(variant.size))) {
        // Numeric (EU shoe) sizing: dedupe/compare as numbers, cap a "(GS)"
        // product at size 40, sort the merged list ascending.
        const have = new Set(variants.map((variant) => normalize(variant.size)));
        const cap = /[(]GS[)]/i.test(this.data.product.title || '') ? 40 : Infinity;
        const ladder = ladderRaw.filter(isNumeric).map(normalize);

        const extras = Array.from(new Set(ladder))
          .filter((size) => !have.has(size) && parseFloat(size) <= cap)
          .map((size) => ({ id: 'extra:' + size, size, sku, price_cents: null, available: false, extra: true }));

        return variants
          .concat(extras)
          .sort((a, b) => parseFloat(normalize(a.size)) - parseFloat(normalize(b.size)));
      }

      // Non-numeric (clothing: S/M/L, or any alpha/alphanumeric size
      // system) - the portal's response is still applied, just without
      // numeric parsing: dedupe case-insensitively against the real
      // variants, keep the portal's own ordering for the extras (there is
      // no single universal letter-size order to invent across every
      // clothing category), and append them after the real variants.
      const normalizeText = (value) => String(value).trim().toUpperCase();
      const have = new Set(variants.map((variant) => normalizeText(variant.size)));
      const seen = new Set();
      const extras = [];
      ladderRaw.forEach((raw) => {
        const key = normalizeText(raw);
        if (!key || have.has(key) || seen.has(key)) return;
        seen.add(key);
        extras.push({ id: 'extra:' + key, size: raw, sku, price_cents: null, available: false, extra: true });
      });

      if (lone && extras.length) return extras;
      return variants.concat(extras);
    }

    // The size-run request is deliberately NOT sent while the page is still loading.
    // It used to fire the moment this element connected - right inside the window where
    // the browser is downloading the page's main photo (its LCP element), on every product
    // page view, including visitors who leave after two seconds. Now it goes out once the
    // page has settled (3 s after `load`, when the browser is idle), or immediately when
    // the visitor opens the dialog - whichever comes first. A response that arrives while
    // the dialog is already open still updates it (see applySizeRun).
    scheduleSizeRun() {
      this.sizeRunStarted = false;
      afterPageSettled(() => this.ensureSizeRun());
    }

    ensureSizeRun() {
      if (this.sizeRunStarted) return;
      this.sizeRunStarted = true;
      this.fetchSizeRun();
    }

    // Live size run from the portal for THIS exact product - see the docs at
    // the top of this file for the full reasoning. Fire-and-forget: runs in
    // the background, doesn't block or delay anything.
    fetchSizeRun() {
      const endpoint = (this.dataset.sizeRunEndpoint || '').trim();
      if (!/^https:[/][/]/i.test(endpoint)) return;

      const sku = (this.data.variants.find((variant) => variant.sku) || {}).sku || '';
      const title = this.data.product.title || '';
      const vendor = this.data.product.vendor || '';
      const type = this.data.product.type || '';

      // All four are always sent, even with a SKU present - title/vendor/
      // type are what let the portal match a model that has no SKU history
      // there yet.
      const query =
        'sku=' + encodeURIComponent(sku) +
        '&title=' + encodeURIComponent(title) +
        '&vendor=' + encodeURIComponent(vendor) +
        '&type=' + encodeURIComponent(type);
      const cacheKey = 'pk_size_run:' + query;

      // Cached per exact request for 10 minutes: the portal's size-run
      // templates don't change minute to minute, so a repeat visit to the
      // same product in this tab shouldn't re-fetch. Independent of
      // whatever Cache-Control the portal's own response may also carry.
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.ts < 10 * 60 * 1000) {
          this.applySizeRun(cached.sizes);
          return;
        }
      } catch (_) {}

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      fetch(endpoint + '?' + query, { credentials: 'omit', signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          // A 404, {ok:false, code:"no_template"}, or any other
          // non-success response all mean the same thing here: the portal
          // has no authoritative size run for this product yet, so it
          // gets zero extra sizes - never a guessed or generic substitute
          // (see the docs at the top of this file). A genuinely empty
          // `sizes: []` (SKU known, no extra sizes) is cached the exact
          // same way as a non-empty result - it's still a real answer.
          const sizes = data && data.ok && Array.isArray(data.sizes) ? data.sizes : [];
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ sizes, ts: Date.now() }));
          } catch (_) {}
          this.applySizeRun(sizes);
        })
        .catch(() => {
          // Network error, timeout, bad JSON, portal down - same
          // treatment as above (zero extra sizes), but not cached, since
          // this wasn't an actual answer from the portal.
        })
        .finally(() => clearTimeout(timer));
    }

    applySizeRun(sizes) {
      if (!sizes.length) return;
      this.sizes = this.buildSizeList(sizes.join(','));
      // The lone "Default Title" variant may have just been replaced by real sizes
      if (this.state) this.state.selected.forEach((id) => { if (!this.variantById(id)) this.state.selected.delete(id); });
      // On a slow connection the fetch can resolve AFTER the visitor has
      // already opened the dialog (open() renders once, synchronously,
      // from whatever this.sizes held at that moment - a late response
      // otherwise updates the data but never touches what's on screen).
      // Re-render only if the dialog is actually open right now.
      if (this.dialog && this.dialog.open) {
        this.renderTabs();
        this.renderSizes();
      }
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setTab(tab.dataset.saTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const other = this.tabs.find((candidate) => candidate !== tab);
          if (other) {
            this.setTab(other.dataset.saTab);
            other.focus();
          }
        });
      });

      const switcher = this.querySelector('[data-sa-switch]');
      if (switcher) switcher.addEventListener('click', () => this.setTab(switcher.dataset.saSwitch));

      this.sizesEl.addEventListener('click', (event) => {
        const chip = event.target.closest('.sa-size');
        if (!chip || chip.disabled) return;
        const id = chip.dataset.id;
        const on = !this.state.selected.has(id);
        if (on) this.state.selected.add(id);
        else this.state.selected.delete(id);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.clearError();
      });

      this.form.addEventListener('submit', (event) => this.submit(event));
      this.emailEl.addEventListener('input', () => this.clearError());

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('sa-scroll-lock');
      });
      this.querySelectorAll('[data-sa-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog.close());
      });
    }

    variantById(id) {
      return this.sizes.find((variant) => variant.id === id);
    }

    // Back in stock: any size we don't currently have (sold-out variants and
    // the extras). Price drop: only real variants (they have a price).
    // A price drop needs a real price to drop from: sold-out sizes often carry 0 Kč.
    isSelectable(variant, tab) {
      return tab === 'price_drop' ? !variant.extra && variant.price_cents > 0 : !variant.available;
    }

    open(detail) {
      if (!this.data || !this.dialog) return;
      this.ensureSizeRun();

      const variants = this.sizes;
      const hasSoldOut = variants.some((variant) => !variant.available);
      const tab =
        detail.tab === 'price_drop' || detail.tab === 'back_in_stock'
          ? detail.tab
          : hasSoldOut
            ? 'back_in_stock'
            : 'price_drop';

      this.state = { tab, selected: new Set() };
      const preselected = detail.variantId ? this.variantById(String(detail.variantId)) : null;
      if (preselected && this.isSelectable(preselected, tab)) this.state.selected.add(preselected.id);

      this.productEl.textContent = this.data.product.title || '';
      this.marketingEl.checked = false;
      this.demoEl.hidden = !this.demo;
      this.formView.hidden = false;
      this.doneView.hidden = true;
      this.doneView.textContent = '';
      this.clearError();
      this.setBusy(false);

      try {
        const saved = window.localStorage.getItem(EMAIL_STORAGE_KEY);
        if (saved && !this.emailEl.value) this.emailEl.value = saved;
      } catch (_) {}

      this.renderTabs();
      this.renderSizes();

      if (!this.dialog.open) {
        document.documentElement.classList.add('sa-scroll-lock');
        this.dialog.showModal();
      }
      this.scrollEl.scrollTop = 0;

      track('stock_alert_open', {
        alert_type: tab,
        alert_sku: (variants[0] && variants[0].sku) || '',
        alert_preselected: Boolean(preselected),
      });
    }

    setTab(tab) {
      if (tab === this.state.tab || !HINTS[tab]) return;
      this.state.tab = tab;
      const keep = new Set();
      this.state.selected.forEach((id) => {
        const variant = this.variantById(id);
        if (variant && this.isSelectable(variant, tab)) keep.add(id);
      });
      this.state.selected = keep;
      this.clearError();
      this.renderTabs();
      this.renderSizes();
    }

    renderTabs() {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.saTab === this.state.tab;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      const lone = this.sizes.length === 1 && this.sizes[0].lone;
      this.hintEl.textContent = (lone ? HINTS_LONE : HINTS)[this.state.tab];
    }

    renderSizes() {
      const { tab, selected } = this.state;
      const variants = tab === 'price_drop' ? this.sizes.filter((variant) => !variant.extra) : this.sizes;
      const nothingToWatch = tab === 'back_in_stock' && this.sizes.every((variant) => variant.available);

      this.sizesEl.textContent = '';
      this.sizesEl.hidden = nothingToWatch;
      this.emptyEl.hidden = !nothingToWatch;
      this.submitEl.disabled = nothingToWatch || this.busy;
      this.legendEl.hidden = !(tab === 'back_in_stock' && !nothingToWatch && this.sizes.some((variant) => variant.available));
      if (nothingToWatch) return;

      // No sizes: nothing to pick, the product itself is watched
      const lone = this.sizes.length === 1 && this.sizes[0].lone;
      if (this.sizesLabelEl) this.sizesLabelEl.hidden = lone;
      this.sizesEl.hidden = lone;
      if (lone) {
        if (this.isSelectable(this.sizes[0], tab)) selected.add(this.sizes[0].id);
        return;
      }

      variants.forEach((variant) => {
        const selectable = this.isSelectable(variant, tab);
        const chip = el('button', 'sa-size');
        chip.type = 'button';
        chip.dataset.id = variant.id;
        chip.disabled = !selectable;
        chip.setAttribute('aria-pressed', selected.has(variant.id) ? 'true' : 'false');

        // Sizes we don't have need no caption (they're the ones you can pick);
        // only the exception - a size that's in stock - is labelled.
        const caption = tab === 'price_drop' ? formatPagePrice(variant.price_cents) : variant.available ? tr('alertInStockCaption', 'skladem') : '';
        chip.append(el('span', 'sa-size__label', variant.size));
        if (caption) chip.append(el('span', 'sa-size__caption', caption));
        this.sizesEl.append(chip);
      });
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    setBusy(flag) {
      this.busy = flag;
      this.submitEl.disabled = flag;
      this.submitEl.textContent = flag ? tr('alertSending', 'Odesílám…') : SUBMIT_LABEL;
      this.submitEl.setAttribute('aria-busy', flag ? 'true' : 'false');
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;

      const { tab, selected } = this.state;
      const email = this.emailEl.value.trim();

      if (selected.size === 0) {
        this.showError(tr('alertErrorSize', 'Vyber aspoň jednu velikost.'));
        return;
      }
      if (!EMAIL_PATTERN.test(email)) {
        this.showError(tr('alertErrorEmail', 'Zadej platný e-mail.'));
        this.emailEl.focus();
        return;
      }

      const items = this.sizes
        .filter((variant) => selected.has(variant.id))
        .map((variant) => {
          const item = { sku: variant.sku || '', size: variant.size, variant_id: variant.extra ? null : variant.id };
          if (tab === 'price_drop') item.baseline_price_cents = variant.price_cents;
          return item;
        });

      // Marketing consent is its own, optional, never pre-ticked choice. When given,
      // the exact wording the visitor saw goes with it (proof of consent).
      const marketing = this.marketingEl.checked;
      const payload = {
        type: tab,
        email,
        consent: true,
        consent_text_version: this.consentVersion,
        marketing_consent: marketing,
        marketing_consent_text: marketing ? this.marketingTextEl.textContent.trim() : null,
        marketing_consent_text_version: marketing ? this.marketingVersion : null,
        source_url: window.location.origin + window.location.pathname,
        website: this.form.elements.website.value,
        items,
        product: this.data.product,
      };

      this.clearError();
      this.setBusy(true);
      try {
        const data = this.demo ? await this.fakeSubmit(items, marketing) : await this.post(payload);
        try {
          window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
        } catch (_) {}
        track('stock_alert_submit', {
          alert_type: tab,
          alert_sizes_count: items.length,
          alert_sku: items[0].sku,
          alert_demo: this.demo,
        });
        this.showDone(data, tab, items);
      } catch (error) {
        this.showError((error && error.userMessage) || GENERIC_ERROR);
      } finally {
        this.setBusy(false);
      }
    }

    async post(payload) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'omit',
          signal: controller.signal,
        });
        let data = null;
        try {
          data = await response.json();
        } catch (_) {}
        if (!response.ok || !data || data.ok !== true) {
          const error = new Error('stock_alert_failed');
          error.userMessage = data && typeof data.message === 'string' && data.message ? data.message : null;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timer);
      }
    }

    async fakeSubmit(items, marketing) {
      await new Promise((resolve) => setTimeout(resolve, 700));
      return {
        ok: true,
        created: items.length,
        already_subscribed: 0,
        in_stock: [],
        marketing_status: marketing ? 'confirmation_sent' : null,
      };
    }

    confirmationText(type, sizes, already) {
      const many = sizes.length > 1;
      const list = sizes.join(', ');
      let text =
        type === 'price_drop'
          ? many
            ? tr('alertDonePdMany', 'Ozveme se e-mailem, když se objeví velikosti [list] za nižší cenu.', { list })
            : tr('alertDonePdOne', 'Ozveme se e-mailem, když se objeví velikost [list] za nižší cenu.', { list })
          : many
            ? tr('alertDoneBisMany', 'Pošleme ti e-mail, jakmile budou velikosti [list] skladem.', { list })
            : tr('alertDoneBisOne', 'Pošleme ti e-mail, jakmile bude velikost [list] skladem.', { list });
      if (already > 0) text += tr('alertDoneAlready', ' Některé z nich už hlídáme.');
      return text;
    }

    showDone(data, type, items) {
      const already = Number(data.already_subscribed) || 0;
      const inStock = Array.isArray(data.in_stock) ? data.in_stock : [];
      const inStockSizes = new Set(inStock.map((entry) => String(entry.size)));
      const watched = items.map((item) => String(item.size)).filter((size) => !inStockSizes.has(size));
      const onlyAlternatives = inStock.length > 0 && watched.length === 0;

      const view = this.doneView;
      view.textContent = '';

      const icon = el('div', 'sa-done__icon');
      icon.innerHTML = CHECK_ICON;
      const heading = el('h2', 'sa-title', onlyAlternatives ? tr('alertDoneHaveIt', 'Máme to skladem') : tr('alertDone', 'Hotovo!'));
      heading.tabIndex = -1;
      view.append(icon, heading);

      if (!onlyAlternatives) {
        view.append(el('p', 'sa-done__text', this.confirmationText(type, watched, already)));
      }

      if (data.marketing_status === 'confirmation_sent') {
        view.append(
          el('p', 'sa-done__text sa-done__text--extra', tr('alertConfirmNewsletter', 'Ještě potvrď odběr novinek: poslali jsme ti e-mail s potvrzovacím odkazem.'))
        );
      }

      if (inStock.length) {
        view.append(el('p', 'sa-label sa-label--spaced', tr('alertInStockNow', 'Skladem teď')));
        const list = el('ul', 'sa-alt');
        inStock.forEach((entry) => {
          const href = sameOriginPath(entry.url);
          if (!href) return;
          const item = el('li');
          const link = el('a', 'sa-alt__link');
          link.href = href;
          link.append(
            el('span', 'sa-alt__size', tr('size', 'Velikost') + ' ' + entry.size),
            el('span', 'sa-alt__price', entry.price_cents ? formatMoney(entry.price_cents) : ''),
            el('span', 'sa-alt__cta', tr('view', 'Zobrazit'))
          );
          item.append(link);
          list.append(item);
        });
        if (list.children.length) view.append(list);
      }

      const closeButton = el('button', 'sa-submit sa-submit--ghost', tr('close', 'Zavřít'));
      closeButton.type = 'button';
      closeButton.addEventListener('click', () => this.dialog.close());
      view.append(closeButton);

      this.formView.hidden = true;
      view.hidden = false;
      heading.focus();
    }
  }

  customElements.define('stock-alert-dialog', StockAlertDialog);

  // This file loads separately from the page now (so the browser can cache it), which
  // opens a short window in which a very quick tap on a sold-out size would find nobody
  // listening. A tiny inline stub in the snippet remembers such taps; replay the latest.
  const queued = window.__pkStockAlertQueue;
  if (queued && queued.length) {
    const detail = queued[queued.length - 1];
    queued.length = 0;
    const dialog = document.querySelector('stock-alert-dialog');
    if (dialog && typeof dialog.open === 'function') dialog.open(detail);
  }
})();
